import axios from 'axios';
import { useConfirm } from 'primevue/useconfirm';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';

import api from '@/api';
import { Loadboard, Shipment, ShipmentSpec } from '@/model';

export interface UseLoadboardsResponse {
  removeLoadboardFromShipmentSpec: (
    loadboard: Loadboard,
    shipmentSpec: ShipmentSpec,
  ) => Promise<void>;
  removeLoadboardFromShipment: (
    loadboard: Loadboard,
    shipment: Shipment,
  ) => Promise<void>;
}

export const useLoadboards = (): UseLoadboardsResponse => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const toast = useToast();

  const removeLoadboardFromShipmentSpec = async (
    loadboard: Loadboard,
    shipmentSpec: ShipmentSpec,
  ): Promise<void> => {
    if (loadboard.id == null || shipmentSpec.id == null) {
      console.error('Loadboard and shipmentSpec ID required');
      toast.addError({
        detail: t('errors.removingParam', {
          param: t('rateShop.loadboards', 1),
        }),
        error: undefined,
      });
      return;
    }
    // TS doesn't recognize the prior null check for IDs so we have to use the ?? -1 pattern here
    const remover = () =>
      api.shipmentSpecLoadboard.remove(
        shipmentSpec.id ?? -1,
        loadboard.id ?? -1,
      );
    const forceRemover = () =>
      api.shipmentSpecLoadboard.forceRemove(
        shipmentSpec.id ?? -1,
        loadboard.id ?? -1,
      );

    await removeLoadboard(remover, forceRemover);
  };

  const removeLoadboardFromShipment = async (
    loadboard: Loadboard,
    shipment: Shipment,
  ): Promise<void> => {
    if (loadboard.id == null || shipment.id == null) {
      console.error('Loadboard and Shipment ID required');
      toast.addError({
        detail: t('errors.removingParam', {
          param: t('rateShop.loadboards', 1),
        }),
        error: undefined,
      });
      return;
    }
    // TS doesn't recognize the prior null check for IDs so we have to use the ?? -1 pattern here
    const remover = () =>
      api.shipmentLoadboard.remove(shipment.id ?? -1, loadboard.id ?? -1);
    const forceRemover = () =>
      api.shipmentLoadboard.forceRemove(shipment.id ?? -1, loadboard.id ?? -1);

    await removeLoadboard(remover, forceRemover);
  };

  const removeLoadboard = async (
    remover: () => Promise<void>,
    forceRemover: () => Promise<void>,
  ): Promise<void> => {
    try {
      await remover();
      toast.addSuccess({
        detail: t('messages.successRemovingParam', {
          param: t('rateShop.loadboards', 1),
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }
      console.error('Error removing loadboard', e);
      await new Promise<void>((resolve) => {
        confirm.require({
          message: t('rateShop.removeLoadboardErrorMessage'),
          icon: 'pi pi-exclamation-triangle text-warning',
          group: 'rygen-confirm-dialog',
          header: t('rateShop.removeLoadboard'),
          accept: async () => {
            try {
              await forceRemover();
            } catch (e) {
              console.error('Error force removing loadboard', e);
              toast.addError({
                detail: t('errors.removingParam', {
                  param: t('rateShop.loadboards', 1),
                }),
                error: e,
              });
            } finally {
              resolve();
            }
          },
          acceptClass: 'p-button-sm p-button-warning',
          acceptLabel: t('rateShop.forceRemove'),
          reject() {
            resolve();
          },
          rejectClass: 'p-button-sm p-button-text',
          rejectLabel: t('common.cancel'),
        });
      });
    }
  };

  return {
    removeLoadboardFromShipmentSpec,
    removeLoadboardFromShipment,
  };
};
